<template>
  <div class="news-create-page">
    <div class="news-create-card">
      <div class="news-create-card__header">
        <h1>Добавить новость</h1>
        <router-link :to="{ name: 'home' }" class="news-create-card__back">
          Вернуться к списку
        </router-link>
      </div>

      <form class="news-create-form" @submit.prevent="submitNews">
        <label class="field">
          <span class="field__label">Источник</span>
          <div class="source-picker">
            <button type="button" class="field__input source-picker__trigger" @click="toggleDropdown">
              <span>{{ selectedSourceLabel }}</span>
              <span class="source-picker__caret">{{ isOpen ? "▴" : "▾" }}</span>
            </button>

            <div v-if="isOpen" class="source-picker__dropdown" @click.stop>
              <div class="source-picker__search">
                <input v-model="searchValue" type="text" placeholder="Поиск источника" @click.stop>
              </div>

              <div class="source-picker__list" @scroll="handleScroll">
                <button
                  v-for="source in sourcesStore.sources"
                  :key="source.id"
                  type="button"
                  class="source-picker__option"
                  :class="{ 'source-picker__option--active': form.sourceId === source.id }"
                  @click="selectSource(source)"
                >
                  {{ source.name }}
                </button>

                <div v-if="isLoading" class="source-picker__state">Загрузка...</div>
                <div v-else-if="!sourcesStore.sources.length" class="source-picker__state">Ничего не найдено</div>
                <div v-else-if="hasMore" class="source-picker__state">Прокрутите вниз, чтобы подгрузить ещё</div>
              </div>
            </div>
          </div>
        </label>

        <label class="field">
          <span class="field__label">Заголовок</span>
          <input v-model="form.title" class="field__input" type="text" required>
        </label>

        <label class="field">
          <span class="field__label">Текст</span>
          <textarea v-model="form.text" class="field__input field__textarea" rows="8" required></textarea>
        </label>

        <label class="field">
          <span class="field__label">URL</span>
          <input v-model="form.url" class="field__input" type="url" placeholder="https://example.com/news">
        </label>

        <label class="field">
          <span class="field__label">Автор</span>
          <input v-model="form.author" class="field__input" type="text" placeholder="Необязательно">
        </label>

        <label class="field">
          <span class="field__label">Дата публикации</span>
          <input v-model="form.published_at" class="field__input" type="datetime-local">
        </label>

        <div class="form-actions">
          <button class="submit-button" type="submit" :disabled="newsStore.isSubmitting">
            {{ newsStore.isSubmitting ? "Отправка..." : "Опубликовать" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useSources } from "@/stores/sources-store";
import { useNewsStore } from "@/stores/news";

const sourcesStore = useSources();
const newsStore = useNewsStore();
const router = useRouter();

const form = reactive({
  sourceId: "",
  title: "",
  text: "",
  url: "",
  author: "",
  published_at: "",
});

const isOpen = ref(false);
const isLoading = ref(false);
const hasMore = ref(true);
const searchValue = ref("");
const selectedSourceLabel = ref("Выберите источник");
const currentPage = ref(1);
let searchTimer = null;

const loadSources = async (reset = false, query = searchValue.value) => {
  if (isLoading.value) return;

  isLoading.value = true;
  const page = reset ? 1 : currentPage.value;
  const response = await sourcesStore.getSources(null, null, 20, page, query, reset);
  const results = response?.results || [];

  if (reset) {
    currentPage.value = 2;
    hasMore.value = results.length >= 20;
  } else {
    currentPage.value = page + 1;
    hasMore.value = results.length >= 20;
  }

  isLoading.value = false;
};

const toggleDropdown = async () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value && !sourcesStore.sources.length) {
    await loadSources(true);
  }
};

const selectSource = (source) => {
  form.sourceId = source.id;
  selectedSourceLabel.value = source.name;
  isOpen.value = false;
};

const handleScroll = (event) => {
  const target = event.target;
  if (!isLoading.value && hasMore.value) {
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 12;
    if (reachedBottom) {
      loadSources(false, searchValue.value);
    }
  }
};

watch(searchValue, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    loadSources(true, searchValue.value);
  }, 300);
});

onMounted(async () => {
  await loadSources(true);
});

onBeforeUnmount(() => {
  clearTimeout(searchTimer);
});

const submitNews = async () => {
  const payload = {
    title: form.title.trim(),
    text: form.text.trim(),
    url: form.url.trim() || null,
    author: form.author.trim() || null,
    published_at: form.published_at ? new Date(form.published_at).toISOString() : null,
  };

  const created = await newsStore.createNews(form.sourceId, payload);

  if (created) {
    form.title = "";
    form.text = "";
    form.url = "";
    form.author = "";
    form.published_at = "";

    await router.push({ name: "home" });
  }
};
</script>

<style scoped lang="scss">
.news-create-page {
  min-height: 100vh;
  padding: 32px 16px;
  background: #f5f7fb;
}

.news-create-card {
  max-width: 760px;
  margin: 0 auto;
  padding: 24px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
}

.news-create-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 24px;
  }
}

.news-create-card__back {
  color: #2f6fed;
  text-decoration: none;
}

.news-create-form {
  display: grid;
  gap: 18px;
}

.field {
  display: grid;
  gap: 8px;
}

.field__label {
  font-weight: 600;
  color: #303645;
}

.field__input {
  width: 100%;
  border: 1px solid #d4d9e2;
  border-radius: 10px;
  padding: 11px 12px;
  font: inherit;
  color: #111827;
  background: #fff;
  box-sizing: border-box;
}

.source-picker {
  position: relative;
}

.source-picker__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  cursor: pointer;
}

.source-picker__caret {
  color: #6b7280;
}

.source-picker__dropdown {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  border: 1px solid #d4d9e2;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.source-picker__search {
  padding: 10px;
  border-bottom: 1px solid #eef2f7;
}

.source-picker__search input {
  width: 100%;
  border: 1px solid #d4d9e2;
  border-radius: 8px;
  padding: 8px 10px;
  box-sizing: border-box;
}

.source-picker__list {
  max-height: 240px;
  overflow-y: auto;
  display: grid;
}

.source-picker__option {
  width: 100%;
  border: none;
  background: #fff;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;

  &:hover,
  &--active {
    background: #f3f7ff;
  }
}

.source-picker__state {
  padding: 10px 12px;
  color: #6b7280;
  font-size: 14px;
}

.field__textarea {
  min-height: 180px;
  resize: vertical;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.submit-button {
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  background: #2f6fed;
  color: #fff;
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}
</style>
